<script setup lang="ts">
import { adaptMapboxFeatureToAddressBody, type AddressBody, MAPBOX_API_CONFIG, type MapboxGeocodeResponse } from '@lib/shared';
import lodashToString from 'lodash-es/toString';

// PROPS
defineProps({
    disabled: { type: Boolean, default: false },
});

// MODEL
const model = defineModel<AddressBody | undefined>({ required: true });

// COMPOSABLES
const { mapboxAccessToken } = useRuntimeConfig().public;
const { notifyError } = useNotification();
const { tt } = useTypedI18n();
const { logError } = useLogs();

// DATA
const searchTerm = ref('');
const searchTermDebounced = refDebounced(searchTerm, 200);

const { data, status, error } = await useFetch(MAPBOX_API_CONFIG.geocodeForward.url, {
    params: { ...MAPBOX_API_CONFIG.geocodeForward.params, q: searchTermDebounced, access_token: mapboxAccessToken },
    transform: (data: MapboxGeocodeResponse): Partial<AddressBody>[] => {
        return (data?.features ?? []).map(feature => adaptMapboxFeatureToAddressBody(feature));
    },
    lazy: true,
    immediate: false,
});

const isLoading = computed(() => status.value === 'pending');
const items = computed<Partial<AddressBody>[]>(() => data.value ?? []);

// WATCHERS
watch(error, value => {
    const errorMessage = isFetchError(value) ? value.data.message : lodashToString(value);
    logError('Error searching fetching mapbox address', { error: errorMessage });
    notifyError({ title: tt('notifications.genericError'), description: tt('vas.LogisticsService.validation.apiError') });
});
</script>

<template>
    <UInputMenu
        v-model="model"
        v-model:search-term="searchTerm"
        :loading="isLoading"
        :items="items"
        icon="lucide:map-pin"
        autocomplete="off"
        size="xl"
        :disabled="disabled"
        label-key="fullAddress"
        ignore-filter
        class="w-full"
    />
</template>
